<template>
  <div
    :class="[
      'info-boires__container mt-60',
      isMobileLayout ? 'info-boires__container--mobile mt-90' : '',
    ]"
  >
    <div class="container text-left subscription-form">
      <div class="flex">
        <h2 class="title text-uppercase pt-38">
          {{ registerFormTitle }}
        </h2>
        <el-image
          :class="[isMobileLayout ? 'ml-8 pt-38' : 'ml-12 pt-35']"
          :style="{ width: isMobileLayout ? '89px' : '128px' }"
          :src="images.logoRed.src"
          :fit="'contain'"
        />
      </div>
      <div
        :class="isMobileLayout ? 'info-boires__subtitle--mobile mt-4' : 'info-boires__subtitle'"
      >
        {{ registerFormSubTitle }}
      </div>

      <mq-layout :mq="['xs', 'sm', 'md']">
        <el-form
          ref="registrationFormMobile"
          :model="form"
          :rules="registerRules"
        >
          <el-row>
            <el-col :span="12" class="pr-10">
              <el-form-item prop="firstName">
                <el-input v-model="form.firstName" :placeholder="registerFormFirstName" />
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item prop="lastName">
                <el-input v-model="form.lastName" :placeholder="registerFormLastName" />
              </el-form-item>
            </el-col>
          </el-row>

          <el-form-item prop="email">
            <el-input v-model="form.email" :placeholder="registerFormEmail" />
          </el-form-item>
          <div class="text-right mt-15">
            <el-button
              type="primary"
              :class="[
                isMobileLayout
                  ? 'button__read-all--mobile'
                  : 'button__read-all',
              ]"
              :loading="isSubmitting"
              @click="submitNewsletter('registrationFormMobile')"
            >
              {{ ctaRegisterForm }}
            </el-button>
          </div>
        </el-form>
      </mq-layout>
      <mq-layout mq="lg+">
        <el-form
          ref="registrationFormDesktop"
          class="flex"
          :model="form"
          :rules="registerRules"
        >
          <el-form-item prop="firstName" class="mr-10" style="width: 200px">
            <el-input v-model="form.firstName" :placeholder="registerFormFirstName" />
          </el-form-item>
          <el-form-item prop="lastName" class="mr-10" style="width: 200px">
            <el-input v-model="form.lastName" :placeholder="registerFormLastName" />
          </el-form-item>
          <el-form-item prop="email" class="mr-10" style="width: 382px">
            <el-input v-model="form.email" :placeholder="registerFormEmail" />
          </el-form-item>
          <el-button
            type="primary"
            class="button__read-all mt-8"
            :loading="isSubmitting"
            @click="submitNewsletter('registrationFormDesktop')"
          >
            {{ ctaRegisterForm }}
          </el-button>
        </el-form>
      </mq-layout>

      <div class="subscription-form__helper">
        <router-link to="/newsletter" class="subscription-form__link">
          {{ advancedNewsletterLabel }}
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { get } from "@/utils/lodash";
import layoutUtils from "@/utils/layout-utils";
import imgLogoRed from "@/assets/images/logo/logo-red.png";
import { Notification } from "element-ui";
import publicIntakeApi from "@/api/public-intake";
import { publicFormsContent } from "@/content/public-pages-content";
import {
  getCurrentMarket,
  getEmbeddedNewsletterCustomerType,
  normalizeLocale,
} from "@/utils/market-utils";

export default {
  data() {
    return {
      form: {
        firstName: null,
        lastName: null,
        email: null,
      },
      images: {
        logoRed: {
          src: imgLogoRed,
        },
      },
      isSubmitting: false,
    };
  },
  computed: {
    ...mapState("configuration", ["actionBar"]),
    ...mapState("user", ["user"]),
    country() {
      return getCurrentMarket(get(this.actionBar, "country"));
    },
    localeKey() {
      return normalizeLocale(this.$i18n.locale);
    },
    newsletterCopy() {
      return publicFormsContent.newsletter[this.localeKey];
    },
    registerFormTitle() {
      return this.$i18n.t("home.homeView.infoRegisterForm.title");
    },
    registerFormSubTitle() {
      return this.$i18n.t("home.homeView.infoRegisterForm.subTitle");
    },
    registerFormFirstName() {
      return this.$i18n.t("home.homeView.infoRegisterForm.firstName");
    },
    registerFormLastName() {
      return this.$i18n.t("home.homeView.infoRegisterForm.lastName");
    },
    registerFormEmail() {
      return this.$i18n.t("home.homeView.infoRegisterForm.email");
    },
    ctaRegisterForm() {
      return this.$i18n.t("home.homeView.infoRegisterForm.cta");
    },
    advancedNewsletterLabel() {
      return this.localeKey === "fr"
        ? "Formulaire complet d’abonnement"
        : "Full newsletter subscription form";
    },
    isMobileLayout() {
      return layoutUtils.isMobileLayout(this.$mq);
    },
    registerRules() {
      const required = this.$i18n.t("users.deliveryAddress.formRequiredMsg");
      const pattern = this.$i18n.t("users.deliveryAddress.formPatternMsg");

      return {
        firstName: [
          {
            required: true,
            message: required.firstName,
          },
        ],
        lastName: [
          {
            required: true,
            message: required.lastName,
          },
        ],
        email: [
          {
            required: true,
            message: required.email,
          },
          {
            pattern: /\S+@\S+\.\S+/,
            message: pattern.email,
          },
        ],
      };
    },
  },
  methods: {
    submitNewsletter(formRef) {
      this.$refs[formRef].validate(async (valid) => {
        if (!valid) {
          return;
        }

        this.isSubmitting = true;

        try {
          await publicIntakeApi.subscribeToNewsletter({
            market: this.country,
            firstName: this.form.firstName,
            lastName: this.form.lastName,
            email: this.form.email,
            phone: "",
            customerType: getEmbeddedNewsletterCustomerType(this.country),
            source: "footer-newsletter",
          });
          Notification({
            title: "Success",
            message: this.newsletterCopy.success,
            type: "success",
          });
          this.resetForm();
        } catch (error) {
          Notification({
            title: "Error",
            message:
              error && error.message === "PUBLIC_INTAKE_API_NOT_CONFIGURED"
                ? this.newsletterCopy.configError
                : this.newsletterCopy.error,
            type: "error",
          });
        } finally {
          this.isSubmitting = false;
        }
      });
    },
    resetForm() {
      this.form = {
        firstName: null,
        lastName: null,
        email: null,
      };
      this.$nextTick(() => {
        if (this.$refs.registrationFormMobile) {
          this.$refs.registrationFormMobile.clearValidate();
        }
        if (this.$refs.registrationFormDesktop) {
          this.$refs.registrationFormDesktop.clearValidate();
        }
      });
    },
  },
  mounted() {
    const rememberedUser = localStorage.getItem("user");
    if (rememberedUser) {
      const userData = JSON.parse(rememberedUser);
      this.form.firstName = userData.user.firstName;
      this.form.lastName = userData.user.lastName;
      this.form.email = userData.user.email;
    }
  },
};
</script>
<style lang="scss" scoped>
@import "@/assets/variables.scss";

.title {
  color: $color-on-background;
}
.info-boires__container {
  background-color: $color-background-area2;
  height: 226px;
}
.info-boires__container--mobile {
  height: 360px !important;
}
.info-boires__subtitle {
  letter-spacing: 0.5px !important;
  line-height: 20px;
  font-weight: 300;
  color: #272421;
  width: 419px;
  height: 42px;
}
.info-boires__subtitle--mobile {
  letter-spacing: 0.5px !important;
  line-height: 20px;
  font-weight: 300;
  color: #272421;
}
.subscription-form__helper {
  margin-top: 12px;
}
.subscription-form__link {
  color: $color-primary;
  text-decoration: underline;
}
</style>
